import React from "react";
import { Tag, Play } from "lucide-react";
import './index.css'
import { CartLoading, Order } from "../../../../redux/cartLoadingSlice";
import Swal from 'sweetalert2';

interface GroupedOrdersViewProps {
  orderGroups: any[];
  orders: Order[];
  cart: CartLoading | undefined;
  onBackToOrderSelection: () => void;
  onConfirmGroups: () => Promise<void>; 
  isCreatingGroupOrder?: boolean;
  setSupervisorTab?: (tab: string) => void;
  updateCartState?: (cartId: string, status: string, gateId?: string, groupedOrderIds?: string[]) => void;
}

const GroupedOrdersView: React.FC<GroupedOrdersViewProps> = ({
  orderGroups,
  orders,
  cart,
  onBackToOrderSelection,
  onConfirmGroups,
  isCreatingGroupOrder,
  setSupervisorTab,
  updateCartState,
}) => {
  const getOrderStatus = (order: Order): string => {
    return order.orderStatus || "Unknown";
  };

  const getOrderReference = (order: Order): string => {
    return order.orderReference || "No reference";
  };

  const getGroupColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: "grouped-order-blue",
      green: "grouped-order-green",
      purple: "grouped-order-purple",
      orange: "grouped-order-orange", 
      pink: "grouped-order-pink",
      teal: "grouped-order-teal",
      indigo: "grouped-order-indigo",
      amber: "grouped-order-amber",
    };
    return colors[color] || colors.blue;
  };

  const getOrderById = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  const handleConfirmGroupsAndStart = async () => {
    if (orderGroups.length === 0) {
      Swal.fire({
        title: 'No Groups',
        text: 'Please create at least one group before proceeding.',
        icon: 'warning',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }
    
    try {
      // Show loading with custom message
      Swal.fire({
        title: 'Creating Groups & Starting Loading',
        text: 'Please wait while we create your order groups and start the loading process...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // First create the groups
      await onConfirmGroups();
      
      // Then start loading if cart exists and updateCartState is provided
      if (cart && updateCartState) {
        await updateCartState(cart.id, "LoadingStarted");
      }
      
      // Close loading and show success
      await Swal.fire({
        title: 'Success!',
        html: 'Order groups created successfully and loading has started!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });
      
      // Add a small delay to ensure the state updates are complete
      setTimeout(() => {
        // Force navigate to cart list - this overrides any automatic navigation
        if (setSupervisorTab) {
          setSupervisorTab("loading");
        }
      }, 100);
      
    } catch (error) {
      console.error("Error confirming groups:", error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to create groups or start loading. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  return (
    <div className="grouped-orders-container">
      <button 
        onClick={onBackToOrderSelection}
        className="grouped-orders-back-button"
      >
        ← Back to Order Selection
      </button>

      <div className="grouped-orders-content">
        <h3 className="grouped-orders-title">Order Groups Overview</h3>
        
        <div className="grouped-orders-list">
          {orderGroups.map(group => (
            <div 
              key={group.id} 
              className={`grouped-order-card ${getGroupColor(group.color)}`}
            >
              <div className="grouped-order-header">
                <div className="grouped-order-info">
                  <Tag className="grouped-order-tag-icon" />
                  <h4 className="grouped-order-name">{group.name}</h4>
                  <span className="grouped-order-count">
                    ({group.orders.length} orders)
                  </span>
                </div>
              </div>

              <div className="grouped-order-orders-grid">
                {group.orders.map((orderId: string) => {
                  const order = getOrderById(orderId);
                  
                  if (!order) {
                    return (
                      <div key={orderId} className="grouped-order-detail">
                        <div className="grouped-order-detail-header">
                          <h5 className="grouped-order-detail-id">Order not found</h5>
                          <span className="grouped-order-priority grouped-order-priority-unknown">
                            Unknown
                          </span>
                        </div>
                        <p className="grouped-order-detail-customer">
                          Order ID: {orderId}
                        </p>
                        <p className="grouped-order-detail-address">
                          Order data unavailable
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div key={orderId} className="grouped-order-detail">
                      <div className="grouped-order-detail-header">
                        <h5 className="grouped-order-detail-id">{getOrderReference(order)}</h5>
                        <span className={`grouped-order-priority grouped-order-priority-${getOrderStatus(order).toLowerCase()}`}>
                          {getOrderStatus(order)}
                        </span>
                      </div>
                  
                      <div className="grouped-order-detail-items">
                        {order.orderItems?.map((item, index) => (
                          <div key={index} className="grouped-order-detail-item">
                            • {item.displayName || "Unknown item"}: {item.quantity} items
                            {item.portionSize && ` (${item.portionSize})`}
                          </div>
                        )) || (
                          <div className="grouped-order-detail-item">
                            • No items in this order
                          </div>
                        )}
                      </div>
                      <div className="grouped-order-detail-total">
                        Total: LKR {order.totalPrice || 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="grouped-orders-actions">
          <button
            onClick={handleConfirmGroupsAndStart}
            disabled={isCreatingGroupOrder}
            className="grouped-orders-confirm-button grouped-orders-start-button"
          >
            <Play className="grouped-orders-confirm-icon" />
            {isCreatingGroupOrder ? "Processing..." : "Confirm Groups & Start Loading"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupedOrdersView;